import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { useMenu } from '../../hooks/useMenu';
import { useApplicantData } from '../../hooks/useApplicantData';
import useApplicationOwnerName from '../../hooks/useApplicationOwnerName';
import useApplicationJointBackupWithholdings from '../../hooks/useApplicationJointBackupWitholding';
import useIsBackUpWithHoldingDispatch from '../../hooks/useIsBackUpWitholdingDispatch';
import useApplicationJointOwnersCountries from '../../hooks/useApplicationJointOwnersCountries';
import useStepPost from '../../hooks/useStepPost';
import { APPLICANT_TYPES, PersonTargetType } from '../../util/constants';
import { getApplicantData } from '../../util/helpers/applicantDataHelper';
import OnBoardingContainer from '../../components/OnBoarding/OnBoardingContainer';
import { HeadingSubtitle } from '../../components/Heading';
import { secureAccountIrsTitle } from '../../util/helpers/onboardingContentHelpers';
import SelectList from '../../components/SelectList/SelectList';
import themeColors from '../../assets/styles/themeColors';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import SaveAndCloseModal from '../../components/Modals/SaveAndCloseModal/SaveAndCloseModal';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import { ACCOUNT_TYPE_GTM } from '../../constants/sessionStorage';
import { ACCOUNT_STEP_COMPLETE, ACCOUNT_STEP_VIEW } from '../../util/gtmEvents';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';

const SecureAccountIRS = ({ applicantType }) => {
  const { activeMenu, activeItem } = useMenu();
  const { t } = useTranslation();
  const ApplicantData = useApplicantData(applicantType);
  const applicantName = useApplicationOwnerName(applicantType);
  const {
    mainBackupWithHolding,
    secondaryBackupWithHolding,
  } = useApplicationJointBackupWithholdings();
  const { backUpWithHoldingDispatch } = useIsBackUpWithHoldingDispatch();
  const { secondaryIdentityCountry } = useApplicationJointOwnersCountries();
  const { onSuccess, onSkipStep } = useStepPost({
    nextPath: activeItem?.nextPath,
    skipStepPath: activeItem?.skipPath,
  });
  const isSecondaryOwner = applicantType === APPLICANT_TYPES.SECONDARY;
  const { BrokerName } = useSelector(selectSettings);
  const [eventType, setEventType] = useState('NoForm');
  const [saveAndCloseModalOpen, setSaveAndCloseModalOpen] = useState(false);

  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  useEffect(() => {
    if (
      applicantType === APPLICANT_TYPES.MAIN &&
      ApplicantData?.PersonName &&
      ApplicantData?.PersonBirthday
    ) {
      gtmStepHelper({
        gtmStepEvent,
        eventName: ACCOUNT_STEP_VIEW,
        stepLabel: 'Secure Identity verification IRS',
        accountType: accountGtmType,
        accountName: ApplicantData?.PersonName,
        accountDateOfBirth: ApplicantData?.PersonBirthday,
      });
      return;
    }
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_VIEW,
      stepLabel: 'Secure Identity verification IRS Second Owner',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGtmEvent = () => {
    if (applicantType === APPLICANT_TYPES.MAIN) {
      gtmStepHelper({
        gtmStepEvent,
        eventName: ACCOUNT_STEP_COMPLETE,
        stepLabel: 'Secure Identity verification IRS',
        accountType: accountGtmType,
        accountName: ApplicantData?.PersonName,
        accountDateOfBirth: ApplicantData?.PersonBirthday,
      });
      return;
    }
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: 'Secure Identity verification IRS Second Owner',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
    });
  };

  const getPersonTargetType = () => {
    switch (applicantType) {
      case APPLICANT_TYPES.MAIN:
      case APPLICANT_TYPES.BUSINESS:
      case APPLICANT_TYPES.MINOR:
        return PersonTargetType[1];
      case APPLICANT_TYPES.SECONDARY:
        return PersonTargetType[2];

      default:
        return '';
    }
  };

  const onSubmit = (value) => {
    if (isSecondaryOwner) {
      if (value) {
        onSuccess();
        return;
      }

      backUpWithHoldingDispatch({
        data: {
          IsSubjectToBackupWithholdings: false,
        },
        shouldUpdate: mainBackupWithHolding || secondaryBackupWithHolding,
        personTargetType: 'both',
        onSuccess: onSkipStep,
      });
      return;
    }

    backUpWithHoldingDispatch({
      data: {
        IsSubjectToBackupWithholdings: value,
      },
      shouldUpdate: getApplicantData(
        ApplicantData,
        'PersonBackupWithHolding',
        'BackupWithHoldingRecord',
      ),
      personTargetType: getPersonTargetType(),
      onSuccess,
    });
  };

  const items = [
    {
      title: t('secureIdentityIRS.no'),
      onClick: () => {
        onSubmit(false);
        handleGtmEvent();
      },
      dataCy: 'button-no',
    },
    {
      title: t('secureIdentityIRS.yes'),
      onClick: () => {
        onSubmit(true);
        handleGtmEvent();
      },
      showDivider: false,
      dataCy: 'button-yes',
    },
  ];

  return (
    <OnBoardingContainer
      activeMenu={activeMenu}
      activeItem={activeItem}
      previousPath={
        isSecondaryOwner && secondaryIdentityCountry !== 'USA'
          ? activeItem?.preSkipPath
          : activeItem?.previousPath
      }
      setEventType={setEventType}
      eventType={eventType}
      setSaveAndCloseModalOpen={setSaveAndCloseModalOpen}
    >
      <Helmet>
        <title>
          {t('titleTagIrs', {
            brokerName: BrokerName,
          })}
        </title>
      </Helmet>
      <HeadingSubtitle mb={32} color={themeColors.colorTextSecondary}>
        {secureAccountIrsTitle(applicantType, applicantName)}
      </HeadingSubtitle>
      <SelectList options={items} />
      {saveAndCloseModalOpen && (
        <SaveAndCloseModal
          isOpen={saveAndCloseModalOpen}
          onClose={() => setSaveAndCloseModalOpen(false)}
        />
      )}
    </OnBoardingContainer>
  );
};

SecureAccountIRS.propTypes = {
  applicantType: PropTypes.string,
};

export default SecureAccountIRS;
